import React, { useCallback, useEffect, useState, useRef } from "react";
import { Html5Qrcode, Html5QrcodeScannerState } from "html5-qrcode";
import { api, formatApiError } from "@/lib/api";
import { Camera, Barcode, CheckCircle, Warning, Prohibit, SignIn, SignOut, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { logger } from "@/lib/logger";

const RESULT_STYLES = {
  verde: { bg: "#22C55E", Icon: CheckCircle },
  rojo: { bg: "#EF4444", Icon: Prohibit },
  naranja: { bg: "#F97316", Icon: Warning },
};

export default function Escaner() {
  const [mode, setMode] = useState("camera"); // camera | scanner | search
  const [direction, setDirection] = useState("in"); // in | out
  const directionRef = useRef("in");
  const [scannerBuf, setScannerBuf] = useState("");
  const scannerInputRef = useRef(null);
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [result, setResult] = useState(null);
  const [match, setMatch] = useState(null);
  const scannerRef = useRef(null);
  const isScanningRef = useRef(false);
  const navigate = useNavigate();

  useEffect(() => { directionRef.current = direction; }, [direction]);

  // Mantiene el input del lector físico enfocado siempre que estamos en modo "scanner"
  useEffect(() => {
    if (mode !== "scanner") return;
    const el = scannerInputRef.current;
    if (el) el.focus();
    const onWindowFocus = () => { if (mode === "scanner" && scannerInputRef.current) scannerInputRef.current.focus(); };
    const onClick = () => { if (mode === "scanner" && scannerInputRef.current) scannerInputRef.current.focus(); };
    window.addEventListener("focus", onWindowFocus);
    document.addEventListener("click", onClick);
    return () => {
      window.removeEventListener("focus", onWindowFocus);
      document.removeEventListener("click", onClick);
    };
  }, [mode]);

  // Búsqueda por nombre/apellidos/nº socio con debounce ligero
  useEffect(() => {
    if (mode !== "search") return;
    const q = searchQ.trim();
    if (q.length < 2) { setSearchResults([]); return; }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/members`, { params: { search: q, limit: 8 } });
        if (!cancelled) setSearchResults(Array.isArray(data) ? data.slice(0, 8) : []);
      } catch (e) {
        if (!cancelled) setSearchResults([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [searchQ, mode]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/matches/active");
        setMatch(data);
      } catch (e) { logger.debug("No active match:", e?.message); }
    })();
  }, []);

  // Contadores de entradas rápidas (2026999/998/997/996) del partido activo
  const [entryCounters, setEntryCounters] = useState({ adulto: 0, joven: 0, nino: 0, cantera: 0, total: 0 });
  const loadCounters = useCallback(async () => {
    try {
      const { data } = await api.get("/access/general-counters");
      setEntryCounters({ ...data.counters, total: data.total });
    } catch (e) { /* silencioso: contadores son informativos */ }
  }, []);
  useEffect(() => { if (match) loadCounters(); }, [match, loadCounters]);

  // Debounce para evitar dobles lecturas: guardamos el último código escaneado y su timestamp
  const lastScanRef = useRef({ code: "", ts: 0 });

  const handleScan = useCallback(async (payload) => {
    // Debounce: bloqueamos si el mismo código llega dos veces en <1500ms
    const code = (payload?.qr_code || payload?.dni || "").toString();
    const now = Date.now();
    if (code && lastScanRef.current.code === code && now - lastScanRef.current.ts < 1500) {
      return; // ignoramos la doble lectura accidental
    }
    lastScanRef.current = { code, ts: now };

    try {
      const body = { ...payload, direction: directionRef.current };
      const { data } = await api.post("/access/scan", body);
      setResult(data);
      // Si fue entrada general especial, refrescamos contadores
      if (data.kind === "general_entry") loadCounters();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }, [loadCounters]);

  const onScanQr = useCallback((qr_code) => handleScan({ qr_code }), [handleScan]);

  useEffect(() => {
    if (mode !== "camera" || !match) return;
    let cancelled = false;
    let html5QrCode = null;
    let started = false;

    // Silenciar el aviso "play() was interrupted because the media was removed"
    // que dispara html5-qrcode si se desmonta el video antes de que play() resuelva.
    const onUnhandled = (e) => {
      const msg = e?.reason?.message || "";
      if (msg.includes("play()") || msg.includes("interrupted because the media was removed")) {
        e.preventDefault();
      }
    };
    window.addEventListener("unhandledrejection", onUnhandled);

    const startScanner = async () => {
      try {
        if (!document.getElementById("qr-reader")) return;
        html5QrCode = new Html5Qrcode("qr-reader");
        scannerRef.current = html5QrCode;
        await html5QrCode.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 250, height: 250 } },
          async (decodedText) => {
            if (isScanningRef.current) return;
            isScanningRef.current = true;
            await onScanQr(decodedText);
            setTimeout(() => { isScanningRef.current = false; }, 2500);
          },
          () => {}
        );
        started = true;
        if (cancelled) {
          // unmounted while starting -> stop now
          try { await html5QrCode.stop(); } catch { /* already stopped */ }
          try { html5QrCode.clear(); } catch { /* cleared */ }
        }
      } catch (e) {
        if (!cancelled) toast.error("No se pudo acceder a la cámara. Usa modo Manual.");
      }
    };

    startScanner();

    return () => {
      cancelled = true;
      window.removeEventListener("unhandledrejection", onUnhandled);
      const s = scannerRef.current;
      scannerRef.current = null;
      if (!s) return;
      if (!started) return; // start() hasn't resolved yet; the inline cancelled check handles cleanup
      try {
        const state = s.getState && s.getState();
        if (state === Html5QrcodeScannerState.SCANNING || state === Html5QrcodeScannerState.PAUSED) {
          s.stop().catch((e) => logger.debug("scanner stop:", e?.message)).finally(() => {
            try { s.clear(); } catch { /* cleared */ }
          });
        } else {
          try { s.clear(); } catch { /* cleared */ }
        }
      } catch {
        // ignore
      }
    };
  }, [mode, match, onScanQr]);

  // Handler del lector físico: al pulsar Enter, procesa el código y limpia
  const onScannerSubmit = (e) => {
    e.preventDefault();
    const val = scannerBuf.trim();
    if (!val) return;
    handleScan({ qr_code: val });
    setScannerBuf("");
    // Vuelve a enfocar inmediatamente para el siguiente escaneo
    setTimeout(() => { if (scannerInputRef.current) scannerInputRef.current.focus(); }, 50);
  };

  if (!match) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <Warning size={48} className="text-[#F97316] mb-4" />
        <h2 className="font-display font-bold text-3xl uppercase">Sin Partido Activo</h2>
        <p className="text-slate-500 mt-2">Debes activar un partido antes de escanear accesos.</p>
        <button onClick={() => navigate("/partidos")} className="mt-6 bg-[#007AFF] px-5 h-11 font-display font-bold uppercase tracking-wide" style={{ borderRadius: 6 }}>
          Ir a Partidos
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[11px] tracking-[0.25em] uppercase text-[#007AFF] font-bold">Control de Accesos</div>
          <h1 className="font-display font-bold text-4xl sm:text-5xl uppercase tracking-tighter">Escáner QR</h1>
          <div className="text-sm text-slate-500 mt-1">Partido: <span className="text-slate-900 font-medium">vs {match.rival}</span></div>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="mode-camera"
            onClick={() => setMode("camera")}
            className={`px-4 h-11 flex items-center gap-2 font-display font-bold uppercase tracking-wide text-sm ${mode === "camera" ? "bg-[#007AFF]" : "border border-slate-200"}`}
            style={{ borderRadius: 6 }}
          >
            <Camera size={18} /> Cámara
          </button>
          <button
            data-testid="mode-scanner"
            onClick={() => setMode("scanner")}
            className={`px-4 h-11 flex items-center gap-2 font-display font-bold uppercase tracking-wide text-sm ${mode === "scanner" ? "bg-[#007AFF]" : "border border-slate-200"}`}
            style={{ borderRadius: 6 }}
          >
            <Barcode size={18} /> Lector
          </button>
          <button
            data-testid="mode-search"
            onClick={() => setMode("search")}
            className={`px-4 h-11 flex items-center gap-2 font-display font-bold uppercase tracking-wide text-sm ${mode === "search" ? "bg-[#007AFF]" : "border border-slate-200"}`}
            style={{ borderRadius: 6 }}
          >
            <MagnifyingGlass size={18} /> Nombre
          </button>
        </div>
      </div>

      {/* Toggle grande ENTRADA / SALIDA */}
      <div
        data-testid="direction-toggle"
        className="grid grid-cols-2 gap-3 bg-white border border-slate-200 p-2 shadow-card"
        style={{ borderRadius: 14 }}
      >
        <button
          data-testid="direction-in-btn"
          onClick={() => setDirection("in")}
          className={`h-20 sm:h-24 flex flex-col items-center justify-center gap-1 font-display font-black uppercase tracking-widest text-xl sm:text-2xl transition ${direction === "in" ? "bg-[#22C55E] text-white shadow-md shadow-emerald-500/30" : "bg-emerald-50 text-emerald-600 hover:bg-emerald-100"}`}
          style={{ borderRadius: 10 }}
        >
          <SignIn size={26} weight="bold" /> Entrada
        </button>
        <button
          data-testid="direction-out-btn"
          onClick={() => setDirection("out")}
          className={`h-20 sm:h-24 flex flex-col items-center justify-center gap-1 font-display font-black uppercase tracking-widest text-xl sm:text-2xl transition ${direction === "out" ? "bg-[#F97316] text-white shadow-md shadow-orange-500/30" : "bg-orange-50 text-orange-600 hover:bg-orange-100"}`}
          style={{ borderRadius: 10 }}
        >
          <SignOut size={26} weight="bold" /> Salida
        </button>
      </div>
      <div
        data-testid="current-mode-banner"
        className={`text-center py-2 px-4 font-display font-bold uppercase tracking-widest text-sm ${direction === "in" ? "bg-emerald-500/10 text-emerald-700" : "bg-orange-500/10 text-orange-700"}`}
        style={{ borderRadius: 10 }}
      >
        Modo actual: {direction === "in" ? "Registro de ENTRADAS" : "Registro de SALIDAS"}
      </div>

      {/* Contadores de entradas rápidas (Adulto/Joven/Niño/Cantera) */}
      <div
        data-testid="general-entries-panel"
        className="bg-white border border-slate-200 shadow-card p-4"
        style={{ borderRadius: 12 }}
      >
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-bold">Entradas escaneadas</div>
            <div className="text-xs text-slate-400">Contadores del partido actual (códigos 2026999/998/997/996)</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-bold">Total</div>
            <div data-testid="general-total" className="font-display font-black text-3xl tracking-tighter text-[#0A2D5C]">{entryCounters.total}</div>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <CounterCard label="Adultos" testid="counter-adulto" value={entryCounters.adulto} color="#0A2D5C" />
          <CounterCard label="Jóvenes" testid="counter-joven" value={entryCounters.joven} color="#2563EB" />
          <CounterCard label="Niños" testid="counter-nino" value={entryCounters.nino} color="#F97316" />
          <CounterCard label="Cantera" testid="counter-cantera" value={entryCounters.cantera} color="#16a34a" />
        </div>
      </div>

      {mode === "scanner" && (
        <form onSubmit={onScannerSubmit} className="bg-white border border-slate-200 p-6 space-y-4 max-w-md mx-auto shadow-card text-center" style={{ borderRadius: 12 }}>
          <div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-bold">{direction === "in" ? "Entrada" : "Salida"} con lector físico</div>
            <h3 className="font-display font-bold text-xl uppercase tracking-tight mt-0.5">Esperando lectura del escáner</h3>
            <p className="text-xs text-slate-500 mt-1">Pasa el código con el lector externo. El código se procesa automáticamente al pulsar Enter (comportamiento estándar de los escáneres tipo teclado).</p>
          </div>
          <div className="relative">
            <Barcode size={48} className={`mx-auto mb-2 ${direction === "in" ? "text-emerald-500" : "text-orange-500"} animate-pulse`} weight="duotone" />
            <input
              ref={scannerInputRef}
              data-testid="scanner-input"
              value={scannerBuf}
              onChange={(e) => setScannerBuf(e.target.value)}
              onBlur={() => setTimeout(() => { if (mode === "scanner" && scannerInputRef.current) scannerInputRef.current.focus(); }, 50)}
              placeholder="Esperando lectura…"
              className="w-full bg-slate-50 border border-slate-200 px-3 h-12 text-slate-900 text-center focus:border-[#2563EB] focus:bg-white outline-none tracking-widest"
              style={{ borderRadius: 8 }}
              autoFocus
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              inputMode="none"
            />
            <div className="text-[10px] text-slate-400 mt-2 uppercase tracking-widest">
              Este campo permanece siempre enfocado — no es necesario hacer clic
            </div>
          </div>
        </form>
      )}
      {mode === "camera" && (
        <div className="bg-white border border-slate-200 p-3 shadow-card" style={{ borderRadius: 12 }}>
          <div id="qr-reader" data-testid="qr-reader" className="mx-auto max-w-md overflow-hidden" />
        </div>
      )}
      {mode === "search" && (
        <div className="bg-white border border-slate-200 p-6 space-y-4 max-w-xl mx-auto shadow-card" style={{ borderRadius: 12 }}>
          <div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-bold">{direction === "in" ? "Entrada" : "Salida"} por nombre</div>
            <h3 className="font-display font-bold text-xl uppercase tracking-tight mt-0.5">Buscar socio por nombre, apellidos o nº</h3>
            <p className="text-xs text-slate-500 mt-1">Para cuando el socio no tenga carné ni DNI a mano. Toca el nombre para registrar el movimiento.</p>
          </div>
          <div className="relative">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="search-input"
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
              placeholder="p.ej. JUAN PÉREZ o 245"
              className="w-full bg-slate-50 border border-slate-200 pl-10 pr-3 h-12 text-slate-900 focus:border-[#2563EB] focus:bg-white outline-none"
              style={{ borderRadius: 8 }}
              autoFocus
              autoComplete="off"
              spellCheck={false}
            />
          </div>
          <div className="min-h-[80px]">
            {searchQ.trim().length < 2 ? (
              <div className="text-center text-slate-400 text-sm py-4">Escribe al menos 2 caracteres…</div>
            ) : searching ? (
              <div className="text-center text-slate-500 text-sm py-4">Buscando…</div>
            ) : searchResults.length === 0 ? (
              <div className="text-center text-slate-500 text-sm py-4">Sin coincidencias</div>
            ) : (
              <ul data-testid="search-results" className="divide-y divide-slate-100 border border-slate-200" style={{ borderRadius: 8 }}>
                {searchResults.map((s) => (
                  <li key={s.id}>
                    <button
                      data-testid={`search-pick-${s.numero_socio}`}
                      onClick={() => { setSearchQ(""); setSearchResults([]); handleScan({ qr_code: s.qr_code }); }}
                      className="w-full text-left px-4 py-3 flex items-center justify-between gap-3 hover:bg-slate-50 transition active:bg-slate-100"
                    >
                      <div className="min-w-0">
                        <div className="font-medium truncate">{s.nombre} {s.apellidos}</div>
                        <div className="text-xs text-slate-500 truncate">
                          Nº {s.numero_socio}
                          {s.dni ? ` • ${s.dni}` : ""}
                          {s.tipo_socio ? ` • ${(s.tipo_socio || "").replace("_", " ")}` : ""}
                        </div>
                      </div>
                      <span
                        className={`shrink-0 px-3 py-1.5 rounded-md text-white font-display font-bold uppercase text-xs tracking-wider ${direction === "in" ? "bg-[#22C55E]" : "bg-[#F97316]"}`}
                      >
                        {direction === "in" ? "Registrar entrada" : "Registrar salida"}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* Result fullscreen overlay */}
      {result && <ResultOverlay result={result} onClose={() => setResult(null)} />}
    </div>
  );
}

function CounterCard({ label, value, color, testid }) {
  return (
    <div
      data-testid={testid}
      className="bg-slate-50 border border-slate-100 p-3 flex flex-col items-center"
      style={{ borderRadius: 10 }}
    >
      <div className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-bold">{label}</div>
      <div className="font-display font-black text-2xl tracking-tighter mt-1" style={{ color }}>{value}</div>
    </div>
  );
}

function ResultOverlay({ result, onClose }) {
  const cfg = RESULT_STYLES[result.result] || RESULT_STYLES.rojo;
  const { Icon } = cfg;
  useEffect(() => {
    // Cierre rápido para permitir escanear al siguiente socio inmediatamente
    const id = setTimeout(onClose, 1800);
    return () => clearTimeout(id);
  }, [onClose]);

  // Título dinámico según resultado + dirección
  let title;
  if (result.kind === "general_entry") {
    title = `ENTRADA ${(result.entry_label || "").toUpperCase()} REGISTRADA`;
  } else if (result.result === "verde") {
    title = result.direction === "out" ? "SALIDA CORRECTA" : "ENTRADA CORRECTA";
  } else if (result.result === "rojo") {
    title = "ACCESO DENEGADO";
  } else {
    title = result.message || "AVISO";
  }

  // Mensaje adicional (subMsg) sólo si aporta info distinta del título
  const subMsg = result.message && result.message.toUpperCase() !== title.toUpperCase() ? result.message : null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
      data-testid={`scan-result-${result.result}`}
    >
      <div
        className="relative w-full max-w-sm flex flex-col items-center text-center text-white shadow-2xl animate-blip overflow-hidden"
        style={{ background: cfg.bg, borderRadius: 24 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="pt-6 pb-2 text-[11px] tracking-[0.3em] uppercase font-bold opacity-80">
          {result.direction === "out" ? "Registro de SALIDA" : "Control de acceso"}
        </div>

        <div className="flex flex-col items-center px-6 pt-2 pb-6 gap-4">
          <div className="h-24 w-24 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
            <Icon size={56} weight="fill" />
          </div>
          <div className="font-display font-black text-4xl uppercase leading-[0.95] tracking-tighter">
            {title}
          </div>
          {subMsg && (
            <div className="font-bold text-sm uppercase opacity-90 tracking-wider">
              {subMsg}
            </div>
          )}
          {result.member && (
            <div className="w-full mt-1 px-4 py-3 bg-white/15 backdrop-blur-sm rounded-xl">
              <div className="font-display font-bold text-xl leading-tight">
                #{result.member.numero_socio} — {result.member.nombre} {result.member.apellidos}
              </div>
              <div className="text-xs opacity-90 capitalize mt-1">
                {result.member.tipo_socio} • Temporada {result.member.temporada}
              </div>
            </div>
          )}
        </div>

        <button
          onClick={onClose}
          data-testid="scan-close-btn"
          className="w-full h-14 bg-white text-slate-900 font-display font-black text-xl uppercase tracking-widest active:bg-slate-100 transition border-t border-white/40"
        >
          Cerrar
        </button>
      </div>
    </div>
  );
}
